import type { QueryClient } from '@tanstack/react-query';
import { librarySectionsQueryOptions } from '@/hooks/queries/useLibrarySectionsQuery';
import libraryItemInfoQueryOptions from '@/hooks/queries/libraryItemInfoQueryOptions';
import buildPresetFromLibraryItem from '@/utils/preset/buildPresetFromLibraryItem';

import type { LibrarySection, Preset } from '@/types';

/**
 * Resolves a preset by slug, cache-first.
 *
 * If the item is already in the cached library sections with its expanded
 * `uploadItems`/`textItems`, the preset is built locally with no network. The
 * `flowId` + expanded-fields guard makes it fall back safely when the cache
 * predates the expand support or the CMS didn't populate the fields.
 *
 * On a miss it delegates to `libraryItemInfoQueryOptions` (the `/flow-landings`
 * detail fetch), which throws on not-found — callers already handle that.
 */
const resolvePresetBySlug = async (
  slug: string,
  queryClient: QueryClient
): Promise<Preset> => {
  const sections = queryClient.getQueryData<LibrarySection[]>(
    librarySectionsQueryOptions.queryKey
  );

  if (sections) {
    for (const section of sections) {
      const item = section.items.find((candidate) => candidate.slug === slug);
      if (
        item?.flowId &&
        (item.uploadItems?.length || item.textItems?.length)
      ) {
        return buildPresetFromLibraryItem(item, section.slug);
      }
    }
  }

  return queryClient.fetchQuery(libraryItemInfoQueryOptions(slug));
};

export default resolvePresetBySlug;
